import { FlightlineEvent } from "../../models/FlightlineEvent"
import { Sortie } from "../../models/Sortie"
import { MissionShippingInfoWithReceiptDto, MissionShippingInfoWrapper } from "../../models/missions/ShippingInfo"
import { MissionActiveFlow, MissionType } from "../../store/types/MissionsTypes"
import { flattenObj } from "../../tools"
import { unplannedLineValue } from "../../utils/FlightlineEventUtils"
import { CustomValidationField, ValidatorModel } from "../../validation"

type MissionKey = keyof MissionType
type MissionShippingInfoKey = keyof MissionShippingInfoWithReceiptDto
type SortieKey = keyof Sortie

/**
 * Since this is not a mission field it's not as type safe
 */
const shipmentKey = "mission_shipping_info"

export enum MissionStep {
    TEAM_IDENTIFICATION = "teamIdentification",
    NOTES = "notes",
    FLIGHT_DATA = "flightData",
    FLIGHTLINE_DETAILS = "flightlineDetails",
}

export const teamRadioOptions = [
    { id: "nv5", label: "NV5 Acquisition Team", value: false },
    { id: "vendor", label: "Non-NV5 Acquisition Team", value: true },
]

export const missionDisplayNames: { [key: string]: string } = {
    mission_shipping_info: "Shipping Info",
    plane_drive_name: "Plane Drive Name",
    backup_drive_name: "Main Backup Drive",
    delivery_drive_name: "Shipped Drive Name",
    office: "Target Office",
    raw_storage_size_gb: "Storage Size",
    base_airport: "Base Airport",
    error_action: "Action Taken",
    error_notes: "Error Notes",
    flight_blog: "Add Blog Entry",
    flightline_events: "Flightline Events",
    flight_notes: "General Notes",
    flight_num: "Flight Number",
    flown_date: "Activity Date",
    manager_id: "Manager On Call",
    operator_1_id: "Sensor Operator 1",
    operator_2_id: "Sensor Operator 2",
    pilot_1_id: "Pilot 1",
    pilot_2_id: "Pilot 2",
    platform_id: "Aircraft/Platform",
    sensor_id_1: "Primary Sensor",
    sensor_id_2: "Secondary Sensor",
    tomorrow_notes: "Tomorrow's Plan",
    vendor_name: "Acquisition Team Name",
    sorties: "Sortie(s)",
}

const teamValidationFields: MissionKey[] = [
    "sensor_id_1",
    "flown_date",
    "platform_id",
    "operator_1_id",
    "pilot_1_id",
    "manager_id",
    "vendor_name",
    "base_airport",
]
const noteValidationFields: MissionKey[] = [
    "flight_notes",
    "error_notes",
    "error_action",
    "tomorrow_notes",
    "flight_blog",
]
const flightDataValidationFields: (MissionKey | MissionShippingInfoKey)[] = [
    "flight_num",
    "sorties",
    "plane_drive_name",
    "backup_drive_name",
]

// vendor == non-nv5 acquisition team
export const getMissionValidatorModel = (
    selectedMission: MissionType,
    flightlineEvents: FlightlineEvent[],
    allShippingInfo: MissionShippingInfoWrapper[],
    activeFlow: MissionActiveFlow | null,
    isVendorFlow: boolean,
): ValidatorModel => {
    const mission = flattenObj({
        ...selectedMission,
        flightline_events: flightlineEvents,
        mission_shipping_info: allShippingInfo.map((info) => info.shipping_info),
    })
    const sortiesRequired = activeFlow !== MissionActiveFlow.NO_FLIGHT && !isVendorFlow

    // vendors vs NV5
    const conditionalTeamFields: MissionKey[] = isVendorFlow
        ? ["vendor_name"]
        : ["platform_id", "operator_1_id", "pilot_1_id", "base_airport"]

    // shipping info and drives
    const conditionalStorageDriveFields: MissionKey[] =
        activeFlow === "no-acquisition" || isVendorFlow ? [] : ["plane_drive_name", "backup_drive_name"]
    let conditionalShippingInfoFields: MissionShippingInfoKey[] = [
        "delivery_drive_name",
        "office",
        "raw_storage_size_gb",
    ]

    if (activeFlow === "no-acquisition") {
        conditionalShippingInfoFields = []
    } else if (isVendorFlow) {
        conditionalShippingInfoFields = ["office"]
    }
    let conditionalCustomShippingValidation: CustomValidationField[] = []

    const conditionalShippingField: string[] = []
    if (conditionalShippingInfoFields.length) {
        conditionalShippingField.push(shipmentKey)

        const requiredFieldsDisplayNames = conditionalShippingInfoFields.map((info) => missionDisplayNames[info])
        conditionalCustomShippingValidation = [
            {
                name: shipmentKey,
                validation: "required_in_array",
                value: conditionalShippingInfoFields,
                text: `${requiredFieldsDisplayNames.join(", ")} cannot be empty`,
            },
        ]
    }

    let conditionalCustomSortieValidation: CustomValidationField[] = []
    if (sortiesRequired) {
        const sortieFields: SortieKey[] = [
            "fe_start_sortie",
            "fe_stop_sortie",
            "duration",
            "start_hobbs",
            "stop_hobbs",
            "active_hobbs",
            "mob_hobbs",
        ]
        conditionalCustomSortieValidation = [
            {
                name: "sorties",
                validation: "required_in_array",
                value: sortieFields,
                text: "Time and hobb fields cannot be empty",
            },
        ]
    }

    const flightlineEventError = flightlineEvents.filter((fl) => {
        return (
            fl.fe_planned_line_ids.length === 0 ||
            (fl.fe_planned_line_ids.includes(unplannedLineValue) && fl.flight_status !== "Ignored")
        )
    })

    return {
        model: mission,
        display_names: missionDisplayNames,
        required_fields: [
            "sensor_id_1",
            "flown_date",
            ...conditionalTeamFields,
            "manager_id",
            "flight_notes",
            "tomorrow_notes",
            "flight_num",
            "flightline_events",
            "reviewer_id",
            ...conditionalStorageDriveFields,
            ...conditionalShippingField,
        ],
        unique_fields: [
            {
                name: "sorties",
                text: "Mission log must include flight data",
                isError: sortiesRequired && selectedMission.sorties.length === 0,
            },
            {
                name: "flightline_events",
                text: "Name, and status fields cannot be empty",
                isError: flightlineEventError.length !== 0,
            },
        ],
        custom_validation_fields: [
            {
                name: "error_notes",
                validation: "regex",
                value: /^[\S\s]{0,65000}$/,
                text: "65000 character limit",
            },
            {
                name: "error_action",
                validation: "regex",
                value: /^[\S\s]{0,65000}$/,
                text: "65000 character limit",
            },
            {
                name: "tomorrow_notes",
                validation: "regex",
                value: /^[\S\s]{0,65000}$/,
                text: "65000 character limit",
            },
            {
                name: "flight_blog",
                validation: "regex",
                value: /^[\S\s]{0,65000}$/,
                text: "65000 character limit",
            },
            {
                name: "raw_storage_size",
                validation: "relationship",
                value: "> 0",
                text: "Value must be greater than 0",
            },
            {
                name: "review_date",
                validation: "requires_other_field_exists",
                value: "reviewer_id",
                text: "Field cannot be empty if reviewer exists",
            },
            {
                name: "reviewer_id",
                validation: "requires_other_field_exists",
                value: "review_date",
                text: "Field cannot be empty if review date exists",
            },
            ...conditionalCustomSortieValidation,
            ...conditionalCustomShippingValidation,
        ],
        groups: {
            [MissionStep.TEAM_IDENTIFICATION]: teamValidationFields,
            [MissionStep.NOTES]: noteValidationFields,
            [MissionStep.FLIGHT_DATA]: [...flightDataValidationFields, shipmentKey],
            [MissionStep.FLIGHTLINE_DETAILS]: ["flightline_events"],
        },
    }
}
